import { useState, useMemo } from 'react'
import { MessageSquare, Trash2, X, Clock, BookOpen, Loader2, Search } from 'lucide-react'
import type { Conversation } from '../lib/types'
import { useAuth } from '../lib/auth-context'

interface SidebarProps {
  conversations: Conversation[]
  currentConversationId?: string
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onDeleteConversation: (id: string) => void
  isOpen: boolean
  onToggle: () => void
  isLoading?: boolean
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function getDateGroup(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const weekAgo = new Date(today.getTime() - 6 * 86400000)

  if (date >= today) return 'Today'
  if (date >= yesterday) return 'Yesterday'
  if (date >= weekAgo) return 'Last 7 days'
  const month = date.toLocaleDateString([], { month: 'long' })
  const year = date.getFullYear() === now.getFullYear() ? '' : ` ${date.getFullYear()}`
  return `${month}${year}`
}

export function Sidebar({ conversations, currentConversationId, onSelectConversation, onNewConversation, onDeleteConversation, isOpen, onToggle, isLoading }: SidebarProps) {
  const { user, profile } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations
    const q = searchQuery.toLowerCase()
    return conversations.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.messages.some(m => m.content.toLowerCase().includes(q))
    )
  }, [conversations, searchQuery])

  const groupedConversations = useMemo(() => {
    const groups = new Map<string, Conversation[]>()
    for (const conv of filteredConversations) {
      const group = getDateGroup(conv.updatedAt)
      if (!groups.has(group)) groups.set(group, [])
      groups.get(group)!.push(conv)
    }
    return groups
  }, [filteredConversations])

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onToggle} />}
      <div className={`fixed left-0 top-0 z-50 flex h-full w-72 flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))] transition-transform duration-300 lg:relative lg:z-0 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--accent))]">
              <BookOpen className="h-3.5 w-3.5 text-white" />
            </div>
            <h2 className="text-sm font-semibold text-[hsl(var(--fg))]">History</h2>
          </div>
          <button onClick={onToggle} className="btn btn-icon btn-ghost lg:hidden">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-3 py-3 space-y-2">
          <button
            onClick={onNewConversation}
            disabled={isLoading}
            className="btn w-full justify-start gap-2 border-dashed border-[hsl(var(--border))] px-3 py-2.5 text-sm text-[hsl(var(--fg-muted))] hover:border-[hsl(var(--accent)/0.5)] hover:text-[hsl(var(--accent))] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquare className="h-4 w-4" />
            )}
            {isLoading ? 'Creating...' : 'New conversation'}
          </button>

          {conversations.length > 0 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="input-field pl-8 pr-3 py-1.5 text-xs text-[hsl(var(--fg))] placeholder:text-[hsl(var(--fg-muted))]"
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-3">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-3 py-12 text-center">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--muted))]">
                <MessageSquare className="h-5 w-5 text-[hsl(var(--fg-muted))]" />
              </div>
              <p className="text-xs text-[hsl(var(--fg-muted))]">
                {searchQuery ? 'No matching conversations' : 'No conversations yet.'}
              </p>
            </div>
          ) : (
            <div className="py-1">
              {Array.from(groupedConversations.entries()).map(([group, convs]) => (
                <div key={group} className="mb-3">
                  <p className="px-3 mb-1 text-[10px] font-medium text-[hsl(var(--fg-muted))] uppercase tracking-wider">
                    {group}
                  </p>
                  {convs.map(conv => (
                    <div key={conv.id} className={`group flex flex-col rounded-xl px-3 py-3 cursor-pointer transition-all duration-200 mb-0.5 ${
                      conv.id === currentConversationId
                        ? 'bg-[hsl(var(--accent-soft))] border border-[hsl(var(--accent)/0.3)] shadow-sm'
                        : 'hover:bg-[hsl(var(--elevated))] border border-transparent hover:border-[hsl(var(--border))]'
                    }`}>
                      <button onClick={() => onSelectConversation(conv.id)} className="flex w-full flex-1 items-start gap-2.5 text-left">
                        <div className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-md ${
                          conv.mode === 'debating' ? 'bg-[hsl(var(--error-bg))]' : conv.mode === 'learning' ? 'bg-[hsl(var(--success-bg))]' : 'bg-[hsl(var(--muted))]'
                        }`}>
                          <MessageSquare className={`h-3.5 w-3.5 ${conv.mode === 'debating' ? 'text-[hsl(var(--error))]' : conv.mode === 'learning' ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--fg-muted))]'}`} />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="truncate text-sm font-medium text-[hsl(var(--fg))] group-hover:text-[hsl(var(--accent))] transition-colors">{conv.title || 'Untitled'}</span>
                          {conv.messages.length > 0 && (
                            <span className="mt-0.5 truncate text-xs text-[hsl(var(--fg-muted))]">{conv.messages[conv.messages.length - 1].content.slice(0, 50)}...</span>
                          )}
                        </div>
                      </button>
                      <div className="mt-1.5 flex items-center justify-between pl-9">
                        <span className="text-[10px] text-[hsl(var(--fg-muted))] opacity-60 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatTime(conv.updatedAt)}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); onDeleteConversation(conv.id) }} className="btn btn-icon btn-sm shrink-0 opacity-0 group-hover:opacity-100 hover:bg-[hsl(var(--error)/0.2)] hover:text-[hsl(var(--error))] active:scale-90" title="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-[hsl(var(--border))] px-4 py-3 space-y-2">
          {user && (
            <div className="flex items-center gap-2 px-2">
              <div className="avatar avatar-ai text-xs font-bold">
                {(profile?.name || user.email || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-[hsl(var(--fg))]">{profile?.name || 'User'}</p>
                <p className="truncate text-[10px] text-[hsl(var(--fg-muted))]">{profile?.religion || 'No religion'}</p>
              </div>
            </div>
          )}
          <p className="px-2 text-[10px] text-[hsl(var(--fg-muted))]">
            Islam Preacher v2.0
          </p>
        </div>
      </div>
    </>
  )
}
